import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "../lib/useLang";
import { useCart } from "../state/CartContext";
import { useLocation } from "../state/LocationContext";
import { formatKWD } from "../lib/format";
import { deliveryEta } from "../lib/delivery";
import { Money } from "../components/Money";
import { QtyStepper } from "../components/QtyStepper";
import { LocationSheet } from "../components/LocationSheet";
import { SmartImage } from "../components/SmartImage";
import { PromoField } from "../components/PromoField";
import { modifierSummary } from "../data/modifiers";

export default function CartPage() {
  const { t, lang, pick } = useLang();
  const { lines, subtotal, discount, discountedSubtotal, setQty } = useCart();
  const { branch, area, mode, fulfillment, quote } = useLocation();
  const isPickup = fulfillment === "pickup";
  const navigate = useNavigate();
  const [showLocation, setShowLocation] = useState(false);

  const q = quote(subtotal);
  const eta = deliveryEta(lines);
  const canCheckout = Boolean(q?.deliverable && !q?.belowMin && lines.length);
  const locLabel =
    mode === "area" && area
      ? pick(area.en, area.ar)
      : branch
        ? pick(branch.name, branch.nameAr)
        : t("cart.setLocation");

  if (lines.length === 0) {
    return (
      <div className="card p-8 text-center">
        <div className="text-4xl">🛒</div>
        <p className="mt-3 text-black/60">{t("cart.empty")}</p>
        <Link to="/" className="btn-primary mt-4">
          {t("cart.browse")}
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <h1 className="display text-2xl text-ink">{t("cart.title")}</h1>

      <div className="card divide-y divide-black/5 px-4">
        {lines.map((l) => {
          const mods = modifierSummary(l.modifiers, lang);
          return (
            <div key={l.uid} className="flex items-center gap-3 py-3">
              <div className="grid size-12 shrink-0 place-items-center overflow-hidden rounded-xl bg-black/[.04] text-lg">
                <SmartImage
                  src={l.imageURL}
                  alt=""
                  className="h-full w-full object-cover"
                  fallback={<span>🍽️</span>}
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-ink">{pick(l.name, l.nameAr)}</div>
                <Money value={l.price} className="text-sm text-black/50" />
                {mods && <div className="truncate text-xs text-black/45">{mods}</div>}
                {l.notes && (
                  <div className="truncate text-xs text-black/45">📝 {l.notes}</div>
                )}
              </div>
              <QtyStepper qty={l.qty} onChange={(qn) => setQty(l.uid, qn)} />
              <Money value={l.price * l.qty} className="price w-20 text-end" />
            </div>
          );
        })}
      </div>

      {/* Location / fee */}
      <div className="card p-4">
        <button
          onClick={() => setShowLocation((s) => !s)}
          className="flex w-full items-center justify-between text-start"
        >
          <span className="flex items-center gap-2 text-sm">
            <span>{isPickup ? "🏪" : "📍"}</span>
            <span className="text-black/50">
              {isPickup ? t("location.pickupFrom") : t("location.deliverTo")}:
            </span>
            <span className="font-semibold text-ink">
              {isPickup && branch ? pick(branch.name, branch.nameAr) : locLabel}
            </span>
          </span>
          <span className="text-sm font-semibold text-brand">{t("location.change")}</span>
        </button>
        {(showLocation || !q) && (
          <div className="mt-3">
            <LocationSheet onDone={() => setShowLocation(false)} />
          </div>
        )}
      </div>

      {/* Promo code */}
      <div className="card space-y-2 p-4">
        <PromoField />
      </div>

      {/* Summary */}
      <div className="card space-y-2 p-4">
        <Row label={t("cart.subtotal")} value={formatKWD(subtotal, lang)} />
        {discount > 0 && (
          <Row label={t("cart.discount")} value={`− ${formatKWD(discount, lang)}`} discount />
        )}
        {q?.deliverable &&
          (isPickup ? (
            branch && (
              <Row label={t("location.pickup")} value={pick(branch.name, branch.nameAr)} />
            )
          ) : (
            <Row label={t("cart.deliveryFee")} value={formatKWD(q.deliveryFee, lang)} />
          ))}
        {q?.deliverable && (
          <Row
            label={t("cart.total")}
            value={formatKWD(discountedSubtotal + q.deliveryFee, lang)}
            bold
          />
        )}
        <div className="flex items-center justify-between pt-1 text-sm">
          <span className="text-black/60">{t("cart.eta")}</span>
          <span className={`font-semibold ${eta.fast ? "text-brand" : "text-ink"}`}>
            {eta.fast ? "⚡ " : ""}~{eta.minutes} {t("common.minutes")}
          </span>
        </div>

        {!q && <p className="pt-1 text-sm text-black/55">{t("cart.setLocation")}</p>}
        {q && !q.deliverable && (
          <p className="pt-1 text-sm font-semibold text-brand">{t("cart.outsideArea")}</p>
        )}
        {q?.belowMin && (
          <p className="pt-1 text-sm font-semibold text-amber-700">
            {t("cart.belowMin", {
              amount: formatKWD(q.amountToMin, lang),
              min: formatKWD(q.minOrder, lang),
            })}
          </p>
        )}

        <button
          className="btn-primary mt-2 w-full"
          disabled={!canCheckout}
          onClick={() => navigate("/checkout")}
        >
          {t("cart.checkout")}
        </button>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  bold,
  discount,
}: {
  label: string;
  value: string;
  bold?: boolean;
  discount?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className={bold ? "font-bold text-ink" : "text-black/60"}>{label}</span>
      <span
        className={
          discount
            ? "font-semibold text-emerald-600"
            : bold
              ? "price text-lg"
              : "font-semibold"
        }
      >
        {value}
      </span>
    </div>
  );
}
